import React from "react";
import { Box, Skeleton, Title } from "@mantine/core";
import {
  IconCreditCard,
  IconWallet,
  IconPackage,
  IconBolt,
  IconCurrencyDollar,
  IconCalendarClock,
  IconCash,
  IconChecks,
  IconInfoCircle,
  IconGift,
} from "@tabler/icons-react";
import { BusinessInfo, SalonContextType } from "../../../types/context";
import { useSalon } from "../../../context/SalonContext";
import { ClientPaymentMethod } from "../../../types/cardOnFile";
import { ClientServicePackage } from "../../../types/packages";
import { ServiceBasisRef } from "../../../utils/packageRedemptionUtils";
import { BookedService } from "../../../types/transactions";
import { SalonPaymentMethod } from "../../../types/salonPaymentMethods";
import { formatCurrency } from "../../../utils/cashDrawerUtils";
import {
  BNPL_METHODS,
  bnplSelectionKey,
  isBnplSelection,
  bnplMethodForSelection,
} from "../../../config/bnpl";
import { CheckoutState, CheckoutStateActions } from "./useCheckoutState";
import MethodPill from "./MethodPill";
import TerminalReveal from "./TerminalReveal";
import CardOnFileReveal from "./CardOnFileReveal";
import PackageReveal from "./PackageReveal";
import GiftCardReveal from "./GiftCardReveal";
import SoftNotePanel from "../../common/surface/SoftNotePanel";
import styles from "./CheckoutPanel.module.css";

interface PaymentMethodPanelProps {
  businessInfo: BusinessInfo;
  state: CheckoutState;
  actions: CheckoutStateActions;
  /**
   * True while ANY method fetch (cards / packages / off-platform) is in
   * flight. Both rows show skeleton pills until every fetch settles, so the
   * full set of options appears at once instead of popping in piecemeal.
   */
  isLoading: boolean;
  /** Saved cards (null while loading / unavailable). */
  cards: ClientPaymentMethod[] | null;
  showCardOnFile: boolean;
  /** All of the client's non-expired packages (empty when none purchased). */
  packages: ClientServicePackage[];
  /** Salon services (for per-basis unit labels in the package reveal). */
  salonServices: ServiceBasisRef[];
  bookedServices: BookedService[];
  /** Salon's selected off-platform methods (sorted). */
  offPlatformMethods: SalonPaymentMethod[];
  /** The amount the charge button would take — gates Affirm bounds. */
  chargeAmount: number;
  /** The client's total spendable gift-card balance (sum of active cards). */
  giftCardBalance: number;
  /** How much gift-card balance will apply to this sale — min(balance, bill). */
  giftCardAmountToApply: number;
  clientId?: string;
  onAddCard: () => void;
  onCardsChanged: () => Promise<void> | void;
}

const OFF_PLATFORM_ICONS: Record<string, React.ReactNode> = {
  Zelle: <IconBolt size={17} />,
  CashApp: <IconCurrencyDollar size={17} />,
  Venmo: <IconWallet size={17} />,
  Check: <IconChecks size={17} />,
};

/**
 * Left checkout paper: "Payment Method" with label-left rows — Preferred
 * Options (Card Present / Card on File / Package) with their reveals, then
 * Off-Platform Options (Cash + salon methods + Affirm), plus contextual
 * note panels. Selection is single-select across ALL groups; sub-selections
 * persist per category while toggling.
 */
export default function PaymentMethodPanel({
  businessInfo,
  state,
  actions,
  isLoading,
  cards,
  showCardOnFile,
  packages,
  salonServices,
  bookedServices,
  offPlatformMethods,
  chargeAmount,
  giftCardBalance,
  giftCardAmountToApply,
  clientId,
  onAddCard,
  onCardsChanged,
}: PaymentMethodPanelProps) {
  const { paymentTerminals } = useSalon() as SalonContextType;
  const terminals = paymentTerminals ?? [];
  const hasTerminal = terminals.length > 0;
  // The Package pill is always offered; it's disabled (with a tooltip) only
  // when the client owns no packages.
  const hasPackages = packages.length > 0;
  // Same pattern for the Gift Card pill: always offered, disabled when the
  // client has no spendable gift-card balance.
  const hasGiftCardBalance = giftCardBalance > 0;

  // BNPL (Affirm) is offered only when BOTH hold: Stripe reports the
  // capability active on the connected account AND the business opted in via
  // Settings → Off-Platform Payments. Hidden entirely otherwise.
  const bnplMethods = BNPL_METHODS.filter(
    () =>
      businessInfo?.stripe_affirm_capability_status === "active" &&
      Boolean(businessInfo?.affirm_payments_enabled),
  );

  const isOffPlatform =
    state.payMethod !== null &&
    state.payMethod !== "Card" &&
    state.payMethod !== "CardOnFile" &&
    state.payMethod !== "Cash" &&
    state.payMethod !== "Package" &&
    state.payMethod !== "GiftCard" &&
    !isBnplSelection(state.payMethod);
  const isAffirm = isBnplSelection(state.payMethod);
  const isPackage = state.payMethod === "Package";
  const isGiftCard = state.payMethod === "GiftCard";
  const isCash = state.payMethod === "Cash";

  // Skeleton pills while the method fetches settle — every option then
  // appears at the same moment.
  if (isLoading) {
    return (
      <Box>
        <Title order={3} className={styles.panelHeading}>
          Payment Method
        </Title>
        <Box className={styles.methodRow}>
          <Box className={styles.methodRowLabel}>Preferred Options</Box>
          <Box className={styles.methodRowOptions}>
            <Box className={styles.pillGroup}>
              <Skeleton height={44} width={140} radius={12} />
              <Skeleton height={44} width={132} radius={12} />
              <Skeleton height={44} width={116} radius={12} />
            </Box>
          </Box>
        </Box>
        <Box className={styles.methodRow}>
          <Box className={styles.methodRowLabel}>Off-Platform Options</Box>
          <Box className={styles.methodRowOptions}>
            <Box className={styles.pillGroup}>
              <Skeleton height={44} width={92} radius={12} />
              <Skeleton height={44} width={100} radius={12} />
              <Skeleton height={44} width={116} radius={12} />
              <Skeleton height={44} width={210} radius={12} />
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Title order={3} className={styles.panelHeading}>
        Payment Method
      </Title>

      {/* Preferred Options */}
      <Box className={styles.methodRow}>
        <Box className={styles.methodRowLabel}>Preferred Options</Box>
        <Box className={styles.methodRowOptions}>
          <Box className={styles.pillGroup}>
            <MethodPill
              icon={<IconCreditCard size={17} />}
              label="Card Present"
              selected={state.payMethod === "Card"}
              onClick={() => actions.setPayMethod("Card")}
              disabled={!hasTerminal}
              tooltip={
                !hasTerminal
                  ? "Please onboard Stripe and connect a Terminal Device to process payments."
                  : undefined
              }
            />
            {showCardOnFile && (
              <MethodPill
                icon={<IconWallet size={17} />}
                label="Card on File"
                selected={state.payMethod === "CardOnFile"}
                onClick={() => actions.setPayMethod("CardOnFile")}
              />
            )}
            <MethodPill
              icon={<IconPackage size={17} />}
              label="Package"
              selected={isPackage}
              onClick={() => actions.setPayMethod("Package")}
              disabled={!hasPackages}
              tooltip={
                !hasPackages
                  ? "User has not purchased any packages."
                  : undefined
              }
            />
            <MethodPill
              icon={<IconGift size={17} />}
              label="Gift Card"
              selected={isGiftCard}
              onClick={() => actions.setPayMethod("GiftCard")}
              disabled={!hasGiftCardBalance}
              tooltip={
                !hasGiftCardBalance
                  ? "Client has no gift card balance to redeem."
                  : undefined
              }
            />
          </Box>

          {/* Reveals for the selected preferred option */}
          {state.payMethod === "Card" && hasTerminal && (
            <TerminalReveal
              terminals={terminals}
              selectedTerminalDeviceId={state.selectedTerminalDeviceId}
              onSelectTerminal={actions.setSelectedTerminalDeviceId}
            />
          )}
          {state.payMethod === "CardOnFile" && clientId && (
            <CardOnFileReveal
              cards={cards ?? []}
              selectedCardId={state.selectedCardId}
              onSelectCard={actions.setSelectedCardId}
              onAddCard={onAddCard}
              onCardsChanged={onCardsChanged}
              clientId={clientId}
            />
          )}
          {isPackage && hasPackages && (
            <PackageReveal
              packages={packages}
              bookedServices={bookedServices}
              salonServices={salonServices}
              timezone={businessInfo?.timezone}
              selectedPackageId={state.selectedPackageId}
              onSelectPackage={actions.setSelectedPackageId}
              packageRedemptions={state.packageRedemptions}
              onSetRedemption={actions.setPackageRedemption}
              onSetRedemptions={actions.setPackageRedemptions}
            />
          )}
          {isGiftCard && hasGiftCardBalance && (
            <GiftCardReveal
              available={giftCardBalance}
              amountToApply={giftCardAmountToApply}
            />
          )}
        </Box>
      </Box>

      {/* Off-Platform Options */}
      <Box className={styles.methodRow}>
        <Box className={styles.methodRowLabel}>Off-Platform Options</Box>
        <Box className={styles.methodRowOptions}>
          <Box className={styles.pillGroup}>
            <MethodPill
              icon={<IconCash size={17} />}
              label="Cash"
              selected={isCash}
              onClick={() => actions.setPayMethod("Cash")}
            />
            {offPlatformMethods.map((method) => (
              <MethodPill
                key={method.id}
                icon={
                  OFF_PLATFORM_ICONS[method.name] ?? (
                    <IconCurrencyDollar size={17} />
                  )
                }
                label={method.name}
                selected={state.payMethod === method.name}
                onClick={() => actions.setPayMethod(method.name)}
              />
            ))}
            {bnplMethods.map((method) => {
              const inRange =
                chargeAmount >= method.minAmount &&
                chargeAmount <= method.maxAmount;
              const disabled = !hasTerminal || !inRange;
              return (
                <MethodPill
                  key={method.key}
                  icon={<IconCalendarClock size={17} />}
                  label={method.label}
                  subLabel="(Buy Now, Pay Later)"
                  selected={state.payMethod === bnplSelectionKey(method)}
                  onClick={() => actions.setPayMethod(bnplSelectionKey(method))}
                  disabled={disabled}
                  tooltip={
                    !hasTerminal
                      ? "Please onboard Stripe and connect a Terminal Device to process payments."
                      : !inRange
                        ? `${method.label} is available for payments between ${formatCurrency(
                            method.minAmount,
                          )} and ${formatCurrency(method.maxAmount)}.`
                        : undefined
                  }
                />
              );
            })}
          </Box>

          {/* BNPL also runs on the reader (renders the QR), so it needs the
              same terminal pick as Card Present. */}
          {isBnplSelection(state.payMethod) && hasTerminal && (
            <TerminalReveal
              terminals={terminals}
              selectedTerminalDeviceId={state.selectedTerminalDeviceId}
              onSelectTerminal={actions.setSelectedTerminalDeviceId}
            />
          )}
        </Box>
      </Box>

      {/* Contextual notes for the selected method */}
      {isOffPlatform && (
        <Box mt={14}>
          <SoftNotePanel icon={<IconInfoCircle size={15} />}>
            Collect the amount via {state.payMethod} outside the app, then
            charge to record it here.
          </SoftNotePanel>
        </Box>
      )}
      {isCash && (
        <Box mt={14}>
          <SoftNotePanel icon={<IconInfoCircle size={15} />}>
            Use “Charge a different amount” to enter the cash received. If it
            exceeds the balance due, you’ll be asked how much of the extra is
            kept as tip; the rest is change.
          </SoftNotePanel>
        </Box>
      )}
      {isAffirm && (
        <Box mt={14}>
          <SoftNotePanel
            icon={<IconCalendarClock size={15} />}
            iconColor="var(--brand-teal)"
          >
            The client completes an{" "}
            {bnplMethodForSelection(state.payMethod)?.label} application on
            their device. Once approved, the balance is split into fixed monthly
            payments and {bnplMethodForSelection(state.payMethod)?.label} pays
            you upfront.
          </SoftNotePanel>
        </Box>
      )}
      {isPackage && (
        <Box mt={14}>
          <SoftNotePanel
            icon={<IconPackage size={15} />}
            iconColor="var(--color-secondary-active)"
          >
            Redeeming a package session covers the eligible service — the
            redeemed units will be deducted from the balance.
          </SoftNotePanel>
        </Box>
      )}

      {isGiftCard && (
        <Box mt={14}>
          <SoftNotePanel
            icon={<IconGift size={15} />}
            iconColor="var(--color-secondary-active)"
          >
            The gift card balance is applied to this sale, drawing the
            soonest-to-expire card first. Any remaining bill is collected with
            another tender.
          </SoftNotePanel>
        </Box>
      )}
    </Box>
  );
}
